import { PaymentElement } from '@stripe/react-stripe-js'
import { useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { Button, TextField, Typography } from '@mui/material'
import { StripePaymentElementOptions } from '@stripe/stripe-js'
import { BASE_URL, STRIPE_SETUP_INTENT_REDIRECT_URL } from '../../config'
import { getIDToken } from '../../utils/AuthUtil'
import { useDispatch, useSelector } from 'react-redux'
import { showUpdatePaymentMethodDialog } from '../../actions/organization'
import { updateCustomerDefaultPaymentMethodAsync } from '../../services/paymentService'
import { RootState } from '../../store'

export default function CheckoutForm(props: any) {
  const dispatch = useDispatch<any>()
  const stripe = useStripe()
  const elements = useElements()
  const [message, setMessage] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const selectedOrganization = useSelector(
    (state: RootState) => state.organization.selectedOrganization,
  )

  const paymentElementOptions: StripePaymentElementOptions = {
    terms: { card: 'never' },
    layout: 'tabs',
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    elements.submit()
    const response = await fetch(`${BASE_URL}/api/payment/createSetupIntent`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        name,
        organization_id: selectedOrganization?.id,
      }),
    })
    const { client_secret, customer } = await response.json()

    const { error, setupIntent } = await stripe.confirmSetup({
      clientSecret: client_secret,
      elements,
      confirmParams: {
        return_url: STRIPE_SETUP_INTENT_REDIRECT_URL,
      },
      redirect: 'if_required',
    })

    if (setupIntent?.status === 'succeeded') {
      setIsLoading(false)
      setMessage('Payment Method Updated Successfully.')
      dispatch(showUpdatePaymentMethodDialog(false))
      dispatch(
        updateCustomerDefaultPaymentMethodAsync({
          customerId: customer,
          paymentMethodId: setupIntent.payment_method,
        }),
      )
      return
    }

    if (error?.type === 'card_error' || error?.type === 'validation_error') {
      setMessage(error.message!)
    } else {
      setMessage('An unexpected error occured.')
    }

    setIsLoading(false)
  }

  return (
    <form id='payment-form' onSubmit={handleSubmit}>
      <Typography
        sx={{
          fontSize: 'large',
          textAlign: 'center',
          mb: 7,
          fontWeight: 'bold',
        }}
      >
        Update Payment Method
      </Typography>
      <Typography
        sx={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
          fontSize: '0.93rem',
          color: '#30313d',
        }}
      >
        Email
      </Typography>
      <TextField
        name='email'
        fullWidth
        size='small'
        sx={{
          height: '8px',
          marginBottom: '34px',
        }}
        onBlur={(e) => setEmail(e.target.value)}
      ></TextField>

      <Typography
        sx={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
          fontSize: '0.93rem',
          color: '#30313d',
        }}
      >
        Card Holder Name
      </Typography>
      <TextField
        name='name'
        fullWidth
        size='small'
        sx={{
          height: '8px',
          marginBottom: '28px',
        }}
        onBlur={(e) => setName(e.target.value)}
      ></TextField>
      <PaymentElement id='payment-element' options={paymentElementOptions} />
      <Button
        variant='contained'
        fullWidth
        disabled={isLoading || !stripe || !elements}
        id='submit'
        onClick={handleSubmit}
      >
        <span id='button-text'>
          {isLoading ? <div className='spinner' id='spinner'></div> : 'Confirm'}
        </span>
      </Button>
      {/* Show any error or success messages */}
      {message && <div id='payment-message'>{message}</div>}
    </form>
  )
}
